import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface MatchNotificationContextType {
  hasNewMatch: boolean;
  setHasNewMatch: (value: boolean) => void;
  showMatchSnackbar: boolean;
  triggerMatchSnackbar: () => void;
  dismissMatchSnackbar: () => void;
  clearMatchBadge: () => void;
}

const MatchNotificationContext = createContext<MatchNotificationContextType | undefined>(undefined);

export const MatchNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasNewMatch, setHasNewMatch] = useState(false);
  const [showMatchSnackbar, setShowMatchSnackbar] = useState(false);

  const triggerMatchSnackbar = useCallback(() => {
    // Always dismiss current and show new (singleton behavior)
    setShowMatchSnackbar(false);
    setTimeout(() => {
      setShowMatchSnackbar(true);
      setHasNewMatch(true);
    }, 100);
  }, []);

  const dismissMatchSnackbar = useCallback(() => {
    setShowMatchSnackbar(false);
  }, []);

  const clearMatchBadge = useCallback(() => {
    setHasNewMatch(false);
  }, []);

  return (
    <MatchNotificationContext.Provider
      value={{
        hasNewMatch,
        setHasNewMatch,
        showMatchSnackbar,
        triggerMatchSnackbar,
        dismissMatchSnackbar,
        clearMatchBadge,
      }}
    >
      {children}
    </MatchNotificationContext.Provider>
  );
};

export const useMatchNotification = () => {
  const context = useContext(MatchNotificationContext);
  if (context === undefined) {
    throw new Error('useMatchNotification must be used within a MatchNotificationProvider');
  }
  return context;
};
